import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Users, Settings, Save, UserPlus, Shield, Trash2, RefreshCw, Key, Eye, EyeOff, LayoutDashboard } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// ============================================================================
// SINGLE SOURCE OF TRUTH - Import from centralized modules config
// When adding new modules, update /src/config/modules.js ONLY
// ============================================================================
import { ALL_MODULES, MODULE_CATEGORIES } from '@/config/modules';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All available dashboard reports/widgets with categories
// Pre-built widgets (General category) are always shown to all users
const ALL_REPORTS = [
  // General (pre-built - always shown to all users)
  { key: 'todays_celebrations', label: "Today's Celebrations", category: 'General', prebuilt: true },
  { key: 'todays_announcements', label: "Today's Announcements", category: 'General', prebuilt: true },
  { key: 'mis_scores', label: 'MIS Scores', category: 'General', prebuilt: true },
  { key: 'action_required', label: 'Action Required', category: 'General', prebuilt: true },
  // Store
  { key: 'pending_gate_entries', label: 'Pending Gate Entries', category: 'Store' },
  { key: 'pending_jc_issues', label: 'Pending JC Issues', category: 'Store' },
  // Purchase
  { key: 'pending_indents', label: 'Pending Indents', category: 'Purchase' },
  { key: 'pending_pos', label: 'Pending POs', category: 'Purchase' },
  // Production
  { key: 'planning_vs_actual', label: 'Planning vs Actual', category: 'Production' },
  { key: 'workers_productivity', label: 'Workers Productivity', category: 'Production' },
  // Quality
  { key: 'ca_pa_assignment', label: 'CA/PA Assignment', category: 'Quality' },
  { key: 'complaints_summary', label: 'Complaints Summary', category: 'Quality' },
  // MIS
  { key: 'tasks_done_report', label: 'Tasks Done Report', category: 'MIS' },
  { key: 'tasks_overdue', label: 'Tasks Overdue', category: 'MIS' },
];

// Pre-built reports that are always shown (cannot be disabled)
const PREBUILT_REPORTS = ALL_REPORTS.filter(r => r.prebuilt).map(r => r.key);

export default function UserManagementPage() {
  const { user, getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openPermissions, setOpenPermissions] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [canApproveVariances, setCanApproveVariances] = useState(false);
  const [permissionsTab, setPermissionsTab] = useState('modules');
  const [saving, setSaving] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Create user from employee
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Show generated password after creating user
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  
  // Password reset - now uses auto-generated password
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetCredentials, setResetCredentials] = useState(null);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/hr-employees`, { headers: getAuthHeader() });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const fetchDesignations = async () => {
    try {
      const response = await axios.get(`${API_URL}/designations`, { headers: getAuthHeader() });
      setDesignations(response.data.filter(d => d.is_active !== false));
    } catch (error) {
      console.error('Failed to fetch designations');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchEmployees();
    fetchDesignations();
  }, []);

  // Get employees without system accounts
  const employeesWithoutAccount = employees.filter(emp => {
    // Check if this employee has an email
    if (!emp.email) return false;
    // Check if a user with this email already exists
    const hasAccount = users.some(u => u.email?.toLowerCase() === emp.email?.toLowerCase());
    return !hasAccount;
  });

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/users/${deleteUser.id}`, { headers: getAuthHeader() });
      toast.success('User deleted successfully');
      setDeleteUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    
    setResettingPassword(true);
    try {
      // Generate a random password
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      const newPassword = Array.from({length: 8}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
      
      await axios.patch(
        `${API_URL}/users/${resetPasswordUser.id}/reset-password`,
        { new_password: newPassword, must_change_password: true },
        { headers: getAuthHeader() }
      );
      
      // Show the new credentials
      setResetCredentials({
        name: resetPasswordUser.name,
        email: resetPasswordUser.email,
        password: newPassword
      });
      
      toast.success(`Password reset successfully for ${resetPasswordUser.name}`);
      setResetPasswordUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }
    
    const employee = employees.find(emp => emp.id === selectedEmployee);
    if (!employee) {
      toast.error('Employee not found');
      return;
    }
    
    setCreatingUser(true);
    try {
      const response = await axios.post(
        `${API_URL}/hr-employees/${employee.id}/create-user`,
        {}, // No password - will be auto-generated
        { headers: getAuthHeader() }
      );
      
      // Show generated credentials
      if (response.data.temporary_password) {
        setGeneratedCredentials({
          name: employee.name,
          email: response.data.email,
          password: response.data.temporary_password
        });
      }
      
      toast.success(`System account created for ${employee.name}`);
      setOpenCreate(false);
      setSelectedEmployee('');
      fetchUsers();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  // Sync user designation from Employee Master
  const syncUserDesignation = async (userItem) => {
    // Find the employee with matching email
    const employee = employees.find(emp => emp.email?.toLowerCase() === userItem.email?.toLowerCase());
    if (!employee) {
      toast.error('No matching employee found in Employee Master');
      return;
    }
    
    // Find the designation from Employee Master
    const designation = designations.find(d => d.name === employee.designation || d.id === employee.designation_id);
    if (!designation) {
      toast.error('Designation not found for this employee');
      return;
    }
    
    try {
      await axios.patch(
        `${API_URL}/users/${userItem.id}/sync-designation`,
        { 
          designation_id: designation.id,
          designation_name: designation.name,
          is_super_admin: designation.is_super_admin || false,
          can_see_financial_data: designation.can_see_financial_data || false,
          allowed_modules: designation.allowed_modules || []
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Synced designation for ${userItem.name}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync designation');
    }
  };

  // Sync ALL users from Employee Master
  const syncAllUsers = async () => {
    setSyncing(true);
    let synced = 0;
    let errors = 0;
    
    for (const userItem of users) {
      const employee = employees.find(emp => emp.email?.toLowerCase() === userItem.email?.toLowerCase());
      if (employee) {
        const designation = designations.find(d => d.name === employee.designation || d.id === employee.designation_id);
        if (designation && designation.name !== userItem.designation_name) {
          try {
            await axios.patch(
              `${API_URL}/users/${userItem.id}/sync-designation`,
              { 
                designation_id: designation.id,
                designation_name: designation.name,
                is_super_admin: designation.is_super_admin || false,
                can_see_financial_data: designation.can_see_financial_data || false,
                allowed_modules: designation.allowed_modules || []
              },
              { headers: getAuthHeader() }
            );
            synced++;
          } catch (e) {
            errors++;
          }
        }
      }
    }
    
    setSyncing(false);
    if (synced > 0 || errors > 0) {
      toast.success(`Synced ${synced} user(s)${errors > 0 ? `, ${errors} error(s)` : ''}`);
      fetchUsers();
    } else {
      toast.info('All users are already in sync');
    }
  };

  const openPermissionsDialog = (userItem) => {
    setSelectedUser(userItem);
    // For owners, always show all modules selected (they have full access)
    if (userItem.is_owner) {
      setSelectedModules(ALL_MODULES.map(m => m.key));
    } else {
      setSelectedModules(userItem.allowed_modules || []);
    }
    // Load reports - include prebuilt by default, plus any user-specific ones
    const userReports = userItem.allowed_reports || [];
    setSelectedReports([...new Set([...PREBUILT_REPORTS, ...userReports])]);
    // Variance Audit per-user flag.
    setCanApproveVariances(!!userItem.can_approve_variances);
    setPermissionsTab('modules');
    setOpenPermissions(true);
  };

  const toggleModule = (moduleKey) => {
    setSelectedModules(prev => {
      if (prev.includes(moduleKey)) {
        return prev.filter(k => k !== moduleKey);
      }
      return [...prev, moduleKey];
    });
  };

  const toggleCategory = (category) => {
    const categoryModules = ALL_MODULES.filter(m => m.category === category).map(m => m.key);
    const allSelected = categoryModules.every(k => selectedModules.includes(k));
    
    if (allSelected) {
      setSelectedModules(prev => prev.filter(k => !categoryModules.includes(k)));
    } else {
      setSelectedModules(prev => [...new Set([...prev, ...categoryModules])]);
    }
  };

  const toggleReport = (reportKey) => {
    // Prebuilt reports cannot be toggled off
    const report = ALL_REPORTS.find(r => r.key === reportKey);
    if (report?.prebuilt) return;
    
    setSelectedReports(prev => {
      if (prev.includes(reportKey)) {
        return prev.filter(k => k !== reportKey);
      }
      return [...prev, reportKey];
    });
  };

  const toggleReportCategory = (category) => {
    const categoryReports = ALL_REPORTS.filter(r => r.category === category && !r.prebuilt).map(r => r.key);
    if (categoryReports.length === 0) return; // All prebuilt, nothing to toggle
    
    const allSelected = categoryReports.every(k => selectedReports.includes(k));
    
    if (allSelected) {
      setSelectedReports(prev => prev.filter(k => !categoryReports.includes(k) || PREBUILT_REPORTS.includes(k)));
    } else {
      setSelectedReports(prev => [...new Set([...prev, ...categoryReports])]);
    }
  };

  const selectAll = () => {
    setSelectedModules(ALL_MODULES.map(m => m.key));
  };

  const clearAll = () => {
    setSelectedModules(['dashboard']);
  };

  const selectAllReports = () => {
    setSelectedReports(ALL_REPORTS.map(r => r.key));
  };

  const clearAllReports = () => {
    // Keep only prebuilt reports
    setSelectedReports(PREBUILT_REPORTS);
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/users/${selectedUser.id}/permissions`,
        { 
          allowed_modules: selectedModules,
          allowed_reports: selectedReports,
          can_approve_variances: canApproveVariances,
        },
        { headers: getAuthHeader() }
      );
      toast.success('Permissions updated successfully');
      setOpenPermissions(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  // Group modules by category
  const groupedModules = ALL_MODULES.reduce((acc, module) => {
    if (!acc[module.category]) acc[module.category] = [];
    acc[module.category].push(module);
    return acc;
  }, {});

  // Group reports by category
  const groupedReports = ALL_REPORTS.reduce((acc, report) => {
    if (!acc[report.category]) acc[report.category] = [];
    acc[report.category].push(report);
    return acc;
  }, {});

  // Get employee designation name for display
  const getEmployeeDesignation = (userItem) => {
    const employee = employees.find(emp => emp.email?.toLowerCase() === userItem.email?.toLowerCase());
    return employee?.designation || null;
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">You don&apos;t have permission to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">User Management</h1>
            <p className="text-muted-foreground">Manage system users and their module access. Users must be created from Employee Master.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={syncAllUsers} disabled={syncing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              Sync All from Employee Master
            </Button>
            <Button onClick={() => setOpenCreate(true)} disabled={employeesWithoutAccount.length === 0}>
              <UserPlus className="w-4 h-4 mr-2" />
              Create User from Employee
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-3">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Users can only be created from the Employee Master. 
              The user&apos;s designation and permissions are inherited from their Employee record and Designation Master.
              Use &quot;Sync All&quot; to update user designations from Employee Master.
            </p>
          </CardContent>
        </Card>

        {/* Users Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>User Designation</TableHead>
                <TableHead>Employee Designation</TableHead>
                <TableHead className="text-center">Modules Access</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No users found</p>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((userItem) => {
                  const empDesignation = getEmployeeDesignation(userItem);
                  const isMismatch = empDesignation && empDesignation !== userItem.designation_name;
                  
                  return (
                    <TableRow key={userItem.id} className={isMismatch ? 'bg-yellow-50' : ''}>
                      <TableCell className="font-medium">{userItem.name}</TableCell>
                      <TableCell>{userItem.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={userItem.is_super_admin ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}>
                          {userItem.designation_name || 'Not Assigned'}
                        </Badge>
                        {userItem.can_see_financial_data && (
                          <Badge variant="outline" className="ml-1 text-xs bg-green-50 text-green-700 border-green-200">
                            Financial
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {empDesignation ? (
                          <Badge variant="outline" className={isMismatch ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : 'bg-gray-50'}>
                            {empDesignation}
                            {isMismatch && ' ⚠️'}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">No employee record</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {userItem.is_owner ? (
                          <Badge variant="default" className="bg-green-600">
                            Full Access (Owner)
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            {(userItem.allowed_modules || []).length} / {ALL_MODULES.length} modules
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isMismatch && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                              onClick={() => syncUserDesignation(userItem)}
                              title="Sync designation from Employee Master"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openPermissionsDialog(userItem)}
                            disabled={userItem.id === user.id && user.email !== 'dinesh@dreamspan.in'}
                            title={userItem.id === user.id && user.email !== 'dinesh@dreamspan.in' ? "Cannot modify your own permissions" : "Manage Permissions"}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Permissions
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => setResetPasswordUser(userItem)}
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteUser(userItem)}
                            disabled={userItem.id === user.id}
                            title={userItem.id === user.id ? "Cannot delete your own account" : "Delete User"}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Create User from Employee Dialog */}
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create User from Employee</DialogTitle>
              <DialogDescription>
                Select an employee from Employee Master to create a system account. 
                Only employees with email addresses who don&apos;t already have accounts are shown.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Select Employee *</Label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employeesWithoutAccount.length === 0 ? (
                        <SelectItem value="none" disabled>
                          No employees without accounts
                        </SelectItem>
                      ) : (
                        employeesWithoutAccount.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name} - {emp.designation || 'No designation'}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedEmployee && (
                  <Card className="bg-muted/50">
                    <CardContent className="py-3 space-y-1">
                      {(() => {
                        const emp = employees.find(e => e.id === selectedEmployee);
                        return emp ? (
                          <>
                            <p className="text-sm"><strong>Name:</strong> {emp.name}</p>
                            <p className="text-sm"><strong>Email:</strong> {emp.email}</p>
                            <p className="text-sm"><strong>Designation:</strong> {emp.designation || 'Not assigned'}</p>
                            <p className="text-sm"><strong>Department:</strong> {emp.department || 'Not assigned'}</p>
                          </>
                        ) : null;
                      })()}
                    </CardContent>
                  </Card>
                )}
                
                <div className="p-3 border rounded-lg bg-blue-50 border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Password will be auto-generated.</strong> The user will be required to change it on first login.
                  </p>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Permissions will be inherited from the employee&apos;s designation in Employee Master.
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpenCreate(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creatingUser || !selectedEmployee}>
                  {creatingUser ? 'Creating...' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Generated Credentials Dialog */}
        <Dialog open={!!generatedCredentials} onOpenChange={(open) => !open && setGeneratedCredentials(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <Shield className="w-5 h-5" />
                User Account Created!
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-4 border rounded-lg bg-green-50 border-green-200">
                <p className="text-sm text-green-800 mb-3">
                  Share these credentials with <strong>{generatedCredentials?.name}</strong>:
                </p>
                <div className="space-y-2 font-mono text-sm">
                  <div className="flex justify-between items-center bg-white p-2 rounded">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-semibold">{generatedCredentials?.email}</span>
                  </div>
                  <div className="flex justify-between items-center bg-white p-2 rounded">
                    <span className="text-gray-600">Password:</span>
                    <span className="font-semibold text-lg tracking-wider">{generatedCredentials?.password}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> The user will be required to change this password on first login.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button onClick={() => {
                const text = `Email: ${generatedCredentials?.email}\nPassword: ${generatedCredentials?.password}`;
                navigator.clipboard.writeText(text);
                toast.success('Credentials copied to clipboard!');
              }} variant="outline">
                Copy Credentials
              </Button>
              <Button onClick={() => setGeneratedCredentials(null)}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteUser} onOpenChange={(open) => !open && setDeleteUser(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deleteUser?.name}</strong>? 
                This action cannot be undone. The user will lose access to the system.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? 'Deleting...' : 'Delete User'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reset Password Confirmation Dialog */}
        <AlertDialog open={!!resetPasswordUser} onOpenChange={(open) => { if (!open) setResetPasswordUser(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Reset Password
              </AlertDialogTitle>
              <AlertDialogDescription>
                Generate a new password for <strong>{resetPasswordUser?.name}</strong> ({resetPasswordUser?.email})?
                <br /><br />
                A new random password will be generated. The user will be required to change it on first login.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleResetPassword} disabled={resettingPassword}>
                {resettingPassword ? 'Generating...' : 'Generate New Password'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reset Password Credentials Dialog */}
        <Dialog open={!!resetCredentials} onOpenChange={(open) => !open && setResetCredentials(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-600">
                <Key className="w-5 h-5" />
                Password Reset Complete!
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                <p className="text-sm text-blue-800 mb-3">
                  Share these new credentials with <strong>{resetCredentials?.name}</strong>:
                </p>
                <div className="space-y-2 font-mono text-sm">
                  <div className="flex justify-between items-center bg-white p-2 rounded">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-semibold">{resetCredentials?.email}</span>
                  </div>
                  <div className="flex justify-between items-center bg-white p-2 rounded">
                    <span className="text-gray-600">New Password:</span>
                    <span className="font-semibold text-lg tracking-wider">{resetCredentials?.password}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> The user will be required to change this password on first login.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button onClick={() => {
                const text = `Email: ${resetCredentials?.email}\nNew Password: ${resetCredentials?.password}`;
                navigator.clipboard.writeText(text);
                toast.success('Credentials copied to clipboard!');
              }} variant="outline">
                Copy Credentials
              </Button>
              <Button onClick={() => setResetCredentials(null)}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Permissions Dialog */}
        <Dialog open={openPermissions} onOpenChange={setOpenPermissions}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Permissions - {selectedUser?.name}
              </DialogTitle>
              <DialogDescription>
                Configure module access and dashboard reports for this user. Changes take effect on next login.
              </DialogDescription>
            </DialogHeader>
            
            <Tabs value={permissionsTab} onValueChange={setPermissionsTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="modules" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Module Permissions
                </TabsTrigger>
                <TabsTrigger value="reports" className="flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4" />
                  Reports Permissions
                </TabsTrigger>
              </TabsList>

              {/* Module Permissions Tab */}
              <TabsContent value="modules" className="space-y-4 mt-4">
                {/* Quick Actions */}
                <div className="flex gap-2 mb-4">
                  <Button size="sm" variant="outline" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button size="sm" variant="outline" onClick={clearAll}>
                    Clear All
                  </Button>
                </div>

                {/* Module Categories */}
                {Object.entries(groupedModules).map(([category, modules]) => {
                  const allInCategorySelected = modules.every(m => selectedModules.includes(m.key));
                  
                  return (
                    <Card key={category}>
                      <CardHeader className="py-3">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={allInCategorySelected}
                            onCheckedChange={() => toggleCategory(category)}
                          />
                          <CardTitle className="text-sm font-semibold">{category}</CardTitle>
                          <Badge variant="outline" className="ml-auto">
                            {modules.filter(m => selectedModules.includes(m.key)).length} / {modules.length}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {modules.map((module) => (
                            <div key={module.key} className="flex items-center gap-2">
                              <Checkbox
                                id={module.key}
                                checked={selectedModules.includes(module.key)}
                                onCheckedChange={() => toggleModule(module.key)}
                              />
                              <label
                                htmlFor={module.key}
                                className="text-sm cursor-pointer"
                              >
                                {module.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </TabsContent>

              {/* Reports Permissions Tab */}
              <TabsContent value="reports" className="space-y-4 mt-4">
                {/* Info Banner */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="py-3">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Pre-built reports (Today&apos;s Celebrations, Announcements, MIS Scores, Action Required) 
                      are always shown on every user&apos;s dashboard and cannot be disabled.
                    </p>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <div className="flex gap-2 mb-4">
                  <Button size="sm" variant="outline" onClick={selectAllReports}>
                    Select All
                  </Button>
                  <Button size="sm" variant="outline" onClick={clearAllReports}>
                    Clear All (Keep Pre-built)
                  </Button>
                </div>

                {/* Report Categories */}
                {Object.entries(groupedReports).map(([category, reports]) => {
                  const nonPrebuiltReports = reports.filter(r => !r.prebuilt);
                  const allInCategorySelected = nonPrebuiltReports.length === 0 || 
                    nonPrebuiltReports.every(r => selectedReports.includes(r.key));
                  const isAllPrebuilt = nonPrebuiltReports.length === 0;
                  
                  return (
                    <Card key={category}>
                      <CardHeader className="py-3">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={allInCategorySelected}
                            onCheckedChange={() => toggleReportCategory(category)}
                            disabled={isAllPrebuilt}
                          />
                          <CardTitle className="text-sm font-semibold">{category}</CardTitle>
                          {isAllPrebuilt && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Always Shown
                            </Badge>
                          )}
                          <Badge variant="outline" className="ml-auto">
                            {reports.filter(r => selectedReports.includes(r.key)).length} / {reports.length}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {reports.map((report) => (
                            <div key={report.key} className="flex items-center gap-2">
                              <Checkbox
                                id={`report-${report.key}`}
                                checked={selectedReports.includes(report.key)}
                                onCheckedChange={() => toggleReport(report.key)}
                                disabled={report.prebuilt}
                              />
                              <label
                                htmlFor={`report-${report.key}`}
                                className={`text-sm ${report.prebuilt ? 'text-muted-foreground' : 'cursor-pointer'}`}
                              >
                                {report.label}
                                {report.prebuilt && <span className="text-xs ml-1 text-green-600">(Pre-built)</span>}
                              </label>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </TabsContent>
            </Tabs>

            {/* Special permissions — Variance Audit (Q4-ii two-checkbox model). */}
            {!selectedUser?.is_owner && (
              <div className="mt-4 p-3 border rounded-lg bg-slate-50">
                <div className="text-sm font-semibold mb-2">Special Permissions</div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={selectedModules.includes('variance_audit')}
                      onCheckedChange={() => toggleModule('variance_audit')}
                      data-testid="va-perm-view"
                    />
                    <span>
                      <span className="font-medium">Variance Report access</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        (view only — adds <code>variance_audit</code> module)
                      </span>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={canApproveVariances}
                      onCheckedChange={(v) => {
                        const checked = !!v;
                        setCanApproveVariances(checked);
                        // Approval implies view — auto-enable the module key.
                        if (checked && !selectedModules.includes('variance_audit')) {
                          setSelectedModules((p) => [...p, 'variance_audit']);
                        }
                      }}
                      data-testid="va-perm-approve"
                    />
                    <span>
                      <span className="font-medium">Variance Approval</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        (view + approve)
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenPermissions(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePermissions} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Permissions'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
